import type { ButtonProps, CardProps, FlexProps } from 'antd';
import type { CSSProperties } from 'react';
import { neutralColors, worktoolDecorations } from '../../config/theme';

/**
 * Comprehensive Component Styling Presets with FastGPT Aesthetics
 * Provides ready-to-use styling presets for common UI patterns
 */

export interface ComponentPresets {
  cards: CardPresets;
  buttons: ButtonPresets;
  typography: TypographyPresets;
  layouts: LayoutPresets;
  interactive: InteractivePresets;
}

// Card Layout Presets using Flex components
export interface CardPresets {
  default: () => CardProps & { style: CSSProperties };
  elevated: () => CardProps & { style: CSSProperties };
  interactive: () => CardProps & { style: CSSProperties };
  minimal: () => CardProps & { style: CSSProperties };
  feature: () => CardProps & { style: CSSProperties };
  testimonial: () => CardProps & { style: CSSProperties };
}

// Button Styling Variants with FastGPT aesthetics
export interface ButtonPresets {
  primary: () => ButtonProps & { style: CSSProperties };
  secondary: () => ButtonProps & { style: CSSProperties };
  outline: () => ButtonProps & { style: CSSProperties };
  ghost: () => ButtonProps & { style: CSSProperties };
  gradient: () => ButtonProps & { style: CSSProperties };
  danger: () => ButtonProps & { style: CSSProperties };
  success: () => ButtonProps & { style: CSSProperties };
  /** 使用中性色（black.500）作为背景的按钮 */
  neutral: () => ButtonProps & { style: CSSProperties };
  large: () => ButtonProps & { style: CSSProperties };
  small: () => ButtonProps & { style: CSSProperties };
  // 动态键（由下方逻辑注入）：black.50..950 和 neutral.50..950
  [key: string]: () => ButtonProps & { style: CSSProperties };
}

// Typography Presets with proper hierarchy
export interface TypographyPresets {
  heroTitle: () => { level?: 1 | 2 | 3 | 4 | 5; style: CSSProperties };
  sectionTitle: () => { level?: 1 | 2 | 3 | 4 | 5; style: CSSProperties };
  cardTitle: () => { level?: 1 | 2 | 3 | 4 | 5; style: CSSProperties };
  subtitle: () => { style: CSSProperties };
  body: () => { style: CSSProperties };
  caption: () => { style: CSSProperties };
  code: () => { code?: boolean; style: CSSProperties };
  link: () => { style: CSSProperties };
}

// Layout Presets using Flex components
export interface LayoutPresets {
  heroSection: () => FlexProps & { style: CSSProperties };
  contentSection: () => FlexProps & { style: CSSProperties };
  cardGrid: (columns?: number) => FlexProps & { style: CSSProperties };
  featureGrid: () => FlexProps & { style: CSSProperties };
  testimonialGrid: () => FlexProps & { style: CSSProperties };
  centeredContainer: (
    maxWidth?: number,
  ) => FlexProps & { style: CSSProperties };
  sidebarLayout: () => FlexProps & { style: CSSProperties };
  stackedLayout: () => FlexProps & { style: CSSProperties };
}

// Interactive State Presets
export interface InteractivePresets {
  hover: CSSProperties;
  active: CSSProperties;
  focus: CSSProperties;
  disabled: CSSProperties;
  loading: CSSProperties;
  selected: CSSProperties;
}

/**
 * Create component presets with theme tokens
 */
export function createComponentPresets(token: any): ComponentPresets {
  // Centralized breakpoint media queries based on AntD tokens
  const mdDown = `@media (max-width: ${token.screenMD || 768}px)`;
  const lgDown = `@media (max-width: ${token.screenLG || 992}px)`;
  // Card presets
  const cards: CardPresets = {
    default: () => ({
      bordered: false,
      style: {
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadow,
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: token.spaceLG || 24,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    elevated: () => ({
      bordered: false,
      style: {
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
        border: 'none',
        padding: token.spaceLG || 24,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    interactive: () => ({
      bordered: false,
      hoverable: true,
      style: {
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadow,
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: token.spaceLG || 24,
        cursor: 'pointer',
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    minimal: () => ({
      bordered: false,
      style: {
        backgroundColor: 'transparent',
        borderRadius: token.borderRadiusLG,
        boxShadow: 'none',
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: token.spaceLG || 24,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    feature: () => ({
      bordered: false,
      style: {
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        boxShadow: worktoolDecorations.cardHoverShadow,
        border: `1px solid ${token.colorPrimaryBorder}`,
        padding: token.spaceXL || 32,
        position: 'relative' as const,
        overflow: 'hidden' as const,
        transition: `all ${token.motionDurationMid}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: worktoolDecorations.primaryGradient,
        },
      },
    }),

    testimonial: () => ({
      bordered: false,
      style: {
        backgroundColor: token.colorBgSpotlight,
        borderRadius: token.borderRadiusLG,
        boxShadow: 'none',
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: token.spaceXL || 32,
        fontStyle: 'italic',
        transition: `all ${token.motionDurationMid}`,
      },
    }),
  };

  // Button presets
  const buttons: ButtonPresets = {
    primary: () => ({
      type: 'primary',
      size: 'large',
      style: {
        background: worktoolDecorations.primaryGradient,
        border: 'none',
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        boxShadow: 'none',
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    secondary: () => ({
      style: {
        backgroundColor: token.colorBgContainer,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: token.colorText,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    outline: () => ({
      style: {
        backgroundColor: 'transparent',
        border: `1px solid ${token.colorPrimary}`,
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: token.colorPrimary,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    ghost: () => ({
      type: 'text',
      style: {
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: token.colorText,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    gradient: () => ({
      style: {
        background: worktoolDecorations.primaryGradient,
        border: 'none',
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: '#ffffff',
        boxShadow: worktoolDecorations.heroGlowShadow,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    danger: () => ({
      danger: true,
      style: {
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    success: () => ({
      style: {
        backgroundColor: token.colorSuccess,
        border: 'none',
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: '#ffffff',
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    neutral: () => ({
      style: {
        backgroundColor: '#6B7280',
        border: 'none',
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: '#ffffff',
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    large: () => ({
      size: 'large',
      style: {
        borderRadius: token.borderRadius,
        height: 52,
        padding: `0 ${token.spaceXL || 32}px`,
        fontWeight: 500,
        fontSize: token.fontSizeLG || 16,
        transition: `all ${token.motionDurationMid}`,
      },
    }),

    small: () => ({
      size: 'small',
      style: {
        borderRadius: token.borderRadiusSM,
        height: token.controlHeightSM || 28,
        padding: `0 ${token.spaceSM || 8}px`,
        fontWeight: 500,
        fontSize: token.fontSizeSM || 12,
        transition: `all ${token.motionDurationMid}`,
      },
    }),
  };

  // 动态生成 black.{50..950} 与 neutral.{50..950} 变体
  const neutralKeys = Object.keys(neutralColors) as unknown as Array<
    keyof typeof neutralColors
  >;
  neutralKeys.forEach((toneKey) => {
    const bg = neutralColors[toneKey];
    const numeric = Number(toneKey);
    const isLight = !Number.isNaN(numeric) && numeric <= 300;
    const textColor = isLight ? '#1A202C' : '#FFFFFF';

    buttons[`black.${toneKey}`] = () => ({
      style: {
        backgroundColor: bg,
        border: 'none',
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: textColor,
        transition: `all ${token.motionDurationMid}`,
      },
    });

    buttons[`neutral.${toneKey}`] = () => ({
      style: {
        backgroundColor: bg,
        border: 'none',
        borderRadius: token.borderRadius,
        height: token.controlHeightLG || 44,
        padding: `0 ${token.spaceLG || 24}px`,
        fontWeight: 500,
        fontSize: token.fontSize || 14,
        color: textColor,
        transition: `all ${token.motionDurationMid}`,
      },
    });
  });

  // Typography presets
  const typography: TypographyPresets = {
    heroTitle: () => ({
      level: 1,
      style: {
        fontSize: (token.fontSizeHeading1 || 48) + 4,
        fontWeight: token.fontWeightStrong || 600,
        lineHeight: token.lineHeightHeading || 1.2,
        color: token.colorTextHeading || token.colorText,
        margin: 0,
        textAlign: 'center' as const,
        background: worktoolDecorations.primaryGradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      },
    }),

    sectionTitle: () => ({
      level: 1,
      style: {
        fontSize: (token.fontSizeHeading2 || 40) + 4,
        fontWeight: token.fontWeightStrong || 600,
        lineHeight: token.lineHeightHeading || 1.2,
        color: token.colorTextHeading || token.colorText,
        margin: `0 0 ${(token.spaceLG || 24) + 4}px 0`,
        textAlign: 'center' as const,
      },
    }),

    cardTitle: () => ({
      level: 3,
      style: {
        fontSize: (token.fontSizeHeading3 || 20) + 2,
        fontWeight: token.fontWeightStrong || 600,
        lineHeight: token.lineHeightHeading || 1.2,
        color: token.colorTextHeading || token.colorText,
        margin: `0 0 ${token.spaceMD || 16}px 0`,
      },
    }),

    subtitle: () => ({
      style: {
        fontSize: (token.fontSizeLG || 24) + 2,
        lineHeight: token.lineHeightLG || 1.5,
        color: token.colorTextSecondary,
        margin: 0,
        textAlign: 'left' as const,
      },
    }),

    body: () => ({
      style: {
        fontSize: (token.fontSize || 14) + 2,
        lineHeight: token.lineHeight || 1.5714,
        color: token.colorText,
        margin: `0 0 ${token.spaceMD || 16}px 0`,
      },
    }),

    caption: () => ({
      style: {
        fontSize: (token.fontSizeSM || 12) + 1,
        lineHeight: token.lineHeightSM || 1.66,
        color: token.colorTextTertiary,
        margin: 0,
      },
    }),

    code: () => ({
      code: true,
      style: {
        fontFamily: token.fontFamilyCode || 'monospace',
        fontSize: token.fontSizeSM || 12,
        backgroundColor: token.colorBgSpotlight,
        padding: '2px 6px',
        borderRadius: token.borderRadiusXS || 4,
        border: `1px solid ${token.colorBorderSecondary}`,
      },
    }),

    link: () => ({
      style: {
        color: token.colorPrimary,
        textDecoration: 'none',
        fontWeight: 500,
        transition: `all ${token.motionDurationMid}`,
        '&:hover': {
          color: token.colorPrimaryHover,
          textDecoration: 'underline',
        },
      },
    }),
  };

  // Layout presets
  const layouts: LayoutPresets = {
    heroSection: () => ({
      vertical: true,
      align: 'center',
      justify: 'center',
      gap: token.spaceXL || 32,
      style: {
        minHeight: '70vh',
        padding: `${token.spaceXXL || 48}px ${token.spaceMD || 16}px`,
        textAlign: 'center' as const,
        background: worktoolDecorations.softGradient,
      },
    }),

    contentSection: () => ({
      vertical: true,
      gap: token.spaceXXL || 48,
      style: {
        padding: `${token.spaceXXL * 1.5 || 72}px 0`,
        maxWidth: 1200,
        margin: '0 auto',
        width: '100%',
      },
    }),

    cardGrid: (columns = 3) => ({
      wrap: 'wrap',
      gap: token.spaceLG || 24,
      style: {
        display: 'flex',
        '& > *': {
          flex: `1 1 calc(${100 / columns}% - ${((columns - 1) * (token.spaceLG || 24)) / columns}px)`,
          minWidth: '280px',
        },
        [mdDown]: {
          '& > *': {
            flex: '1 1 100%',
          },
        },
      },
    }),

    featureGrid: () => ({
      wrap: 'wrap',
      gap: token.spaceXL || 32,
      style: {
        display: 'flex',
        '& > *': {
          flex: '1 1 calc(33.333% - 22px)',
          minWidth: '300px',
        },
        [lgDown]: {
          '& > *': {
            flex: '1 1 calc(50% - 16px)',
          },
        },
        [mdDown]: {
          '& > *': {
            flex: '1 1 100%',
          },
        },
      },
    }),

    testimonialGrid: () => ({
      wrap: 'wrap',
      gap: token.spaceLG || 24,
      style: {
        display: 'flex',
        '& > *': {
          flex: '1 1 calc(50% - 12px)',
          minWidth: '320px',
        },
        [mdDown]: {
          '& > *': {
            flex: '1 1 100%',
          },
        },
      },
    }),

    centeredContainer: (maxWidth = 1200) => ({
      vertical: false,
      justify: 'center',
      style: {
        maxWidth,
        margin: '0 auto',
        padding: `0 ${token.spaceMD || 16}px`,
        width: '100%',
      },
    }),

    sidebarLayout: () => ({
      vertical: false,
      gap: token.spaceXL || 32,
      style: {
        alignItems: 'flex-start',
        '& > :first-child': {
          flex: '0 0 300px',
        },
        '& > :last-child': {
          flex: '1 1 auto',
        },
        [mdDown]: {
          flexDirection: 'column',
          '& > :first-child': {
            flex: '1 1 auto',
            width: '100%',
          },
        },
      },
    }),

    stackedLayout: () => ({
      vertical: true,
      gap: token.spaceLG || 24,
      style: {
        width: '100%',
      },
    }),
  };

  // Interactive state presets
  const interactive: InteractivePresets = {
    hover: {
      backgroundColor: worktoolDecorations.hoverOverlay,
      transform: 'translateY(-2px)',
      boxShadow: worktoolDecorations.cardHoverShadow,
      transition: `all ${token.motionDurationMid}`,
    },

    active: {
      backgroundColor: worktoolDecorations.activeOverlay,
      transform: 'translateY(0)',
      transition: `all ${token.motionDurationFast}`,
    },

    focus: {
      outline: 'none',
      boxShadow: worktoolDecorations.focusRing,
      transition: `all ${token.motionDurationFast}`,
    },

    disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      pointerEvents: 'none' as const,
    },

    loading: {
      opacity: 0.8,
      cursor: 'wait',
      pointerEvents: 'none' as const,
    },

    selected: {
      backgroundColor: token.colorPrimaryBg,
      borderColor: token.colorPrimary,
      color: token.colorPrimary,
    },
  };

  return {
    cards,
    buttons,
    typography,
    layouts,
    interactive,
  };
}
